import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import Drawer from "@/components/Drawer";
import { LoadingState } from "@/components/LoadingState";
import { unitApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import type { Unit } from "@/types";

export default function BuildingDetailPage() {
  const navigate = useNavigate();
  const { orgId, buildingId } = useParams<{ orgId?: string; buildingId: string }>();
  const user = useAuthStore((s) => s.user)!;
  const canManage = user.is_staff || !!user.role?.can_manage_units;
  const backHref = orgId ? `/staff/organisations/${orgId}/buildings` : "/buildings";
  const unitHref = (uId: string) =>
    orgId ? `/staff/organisations/${orgId}/buildings/${buildingId}/units/${uId}`
          : `/buildings/${buildingId}/units/${uId}`;
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const form = useForm<Partial<Unit>>({ defaultValues: { is_occupied: false, is_common_area: false } });

  const load = async () => {
    if (!buildingId) return;
    setLoading(true);
    setUnits(await unitApi.list(buildingId));
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, [buildingId]);

  const submit = form.handleSubmit(async (values) => {
    if (!buildingId) return;
    try {
      await unitApi.create(buildingId, values);
      toast.success("Unit created");
      setDrawerOpen(false);
      form.reset({ is_occupied: false, is_common_area: false });
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  });

  const columns: Column<Unit>[] = [
    { key: "number", header: "Unit", render: (u) => u.unit_number },
    { key: "floor", header: "Floor", render: (u) => u.floor ?? "—" },
    { key: "tenant", header: "Tenant", render: (u) => u.tenant_name ?? "—" },
    {
      key: "type",
      header: "Type",
      render: (u) =>
        u.is_common_area ? (
          <span className="badge bg-amber-50 text-amber-800">Common · {u.common_area_type ?? "area"}</span>
        ) : (
          <span className="badge bg-slate-100 text-slate-700">Leasable</span>
        ),
    },
    {
      key: "occupied",
      header: "Status",
      render: (u) =>
        u.is_occupied ? (
          <span className="badge bg-brand-50 text-brand-800">Occupied</span>
        ) : (
          <span className="badge bg-slate-100 text-slate-600">Vacant</span>
        ),
    },
  ];

  return (
    <AppLayout>
      <div className="mb-4">
        <Link to={backHref} className="text-sm text-brand-700 hover:underline">← Back to buildings</Link>
      </div>
      <PageHeader
        title="Units"
        subtitle={`${units.length} ${units.length === 1 ? "unit" : "units"} — click any row to manage meters`}
        actions={
          canManage && (
            <button className="btn-primary" type="button" onClick={() => setDrawerOpen(true)}>
              <Plus size={16} /> Add unit
            </button>
          )
        }
      />
      {loading ? <LoadingState /> : (
        <DataTable
          columns={columns}
          rows={units}
          emptyMessage="No units yet"
          onRowClick={(u) => navigate(unitHref(u.id))}
        />
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Add unit"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" type="button" onClick={() => setDrawerOpen(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={submit}>Create</button>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="label">Unit number</label>
            <input className="input" {...form.register("unit_number", { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Floor</label>
              <input type="number" className="input" {...form.register("floor", { valueAsNumber: true })} />
            </div>
            <div>
              <label className="label">Floor size (m²)</label>
              <input type="number" step="0.01" className="input" {...form.register("floor_size_m2", { valueAsNumber: true })} />
            </div>
          </div>
          <div>
            <label className="label">Tenant name</label>
            <input className="input" {...form.register("tenant_name")} />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...form.register("is_occupied")} /> Occupied
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...form.register("is_common_area", {
                  onChange: (e) => {
                    if (!e.target.checked) form.setValue("common_area_type", undefined);
                  },
                })}
              /> Common area
            </label>
          </div>
          {form.watch("is_common_area") && (
            <div>
              <label className="label">Common area type</label>
              <select className="input" {...form.register("common_area_type", { required: true })}>
                <option value="">Select a type…</option>
                {["lobby","parking","food_hall","bathroom","corridor","gym","plant_room","other"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}
        </form>
      </Drawer>
    </AppLayout>
  );
}
